import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronRight, Users, CheckCircle2, XCircle,
    Clock, Search, Filter, ShieldAlert, PartyPopper,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import PageHeader from '@/components/ux/PageHeader';
import StatusBadge from '@/components/ux/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table, TableBody, TableCell, TableHead,
    TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

function PipelineListSkeleton() {
    return (
        <div className="space-y-5">
            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <Card key={i}>
                        <CardContent className="p-4 space-y-2">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-7 w-10" />
                        </CardContent>
                    </Card>
                ))}
            </div>
            {/* Search bar */}
            <Skeleton className="h-9 w-full max-w-xs" />
            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="divide-y divide-border">
                        {/* Header */}
                        <div className="flex gap-4 px-4 py-3">
                            {[140, 120, 80, 80, 90, 70].map((w, i) => (
                                <Skeleton key={i} className="h-3" style={{ width: w }} />
                            ))}
                        </div>
                        {/* Rows */}
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="flex items-center gap-4 px-4 py-3.5">
                                <div className="space-y-1.5" style={{ width: 140 }}>
                                    <Skeleton className="h-3.5 w-28" />
                                    <Skeleton className="h-2.5 w-36" />
                                </div>
                                <Skeleton className="h-3 w-28" />
                                <Skeleton className="h-5 w-16 rounded-full" />
                                <Skeleton className="h-5 w-16 rounded-full" />
                                <Skeleton className="h-3 w-20" />
                                <Skeleton className="h-7 w-16 rounded-md ml-auto" />
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

const STATUS_ICON = {
    in_progress: <Clock className="h-4 w-4 text-blue-500" />,
    passed:      <CheckCircle2 className="h-4 w-4 text-green-500" />,
    rejected:    <XCircle className="h-4 w-4 text-red-500" />,
    on_hold:     <Clock className="h-4 w-4 text-amber-500" />,
    offer_made:  <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
    terminated:  <ShieldAlert className="h-4 w-4 text-red-500" />,
};

const DECISION_COLOR = {
    hire:     'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20',
    reject:   'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/20',
    on_hold:  'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20',
    pending:  'bg-muted text-muted-foreground',
};

function latestPerRoundNumber(rounds) {
    const map = {};
    for (const r of rounds || []) {
        const rn = r.roundNumber;
        const prev = map[rn];
        if (!prev || (r.attemptNumber || 1) >= (prev.attemptNumber || 1)) map[rn] = r;
    }
    return Object.values(map).sort((a, b) => a.roundNumber - b.roundNumber);
}

function RoundDots({ rounds }) {
    const latest = latestPerRoundNumber(rounds);
    if (!latest.length) return <span className="text-xs text-muted-foreground">—</span>;
    return (
        <div className="flex items-center gap-1">
            {latest.map((r) => (
                <span
                    key={`${r.roundNumber}-${r.attemptNumber || 1}`}
                    title={`${r.roundName || `Round ${r.roundNumber}`}${(r.attemptNumber || 1) > 1 ? ` (attempt ${r.attemptNumber})` : ''}: ${r.status}`}
                    className={cn(
                        'h-2.5 w-2.5 rounded-full border',
                        r.status === 'passed'      && 'bg-green-500 border-green-600',
                        r.status === 'failed'      && 'bg-red-500 border-red-600',
                        r.status === 'terminated'  && 'bg-red-500 border-red-600',
                        r.status === 'expired'     && 'bg-orange-400 border-orange-500',
                        r.status === 'completed'   && 'bg-blue-400 border-blue-500',
                        r.status === 'in_progress' && 'bg-blue-300 border-blue-400 animate-pulse',
                        r.status === 'scheduled'   && 'bg-muted-foreground/30 border-muted-foreground/50',
                        r.status === 'cancelled'   && 'bg-gray-300 border-gray-400',
                        r.status === 'no_show'     && 'bg-gray-400 border-gray-500',
                    )}
                />
            ))}
        </div>
    );
}

export default function CandidateProcessPage() {
    const navigate = useNavigate();
    const [processes, setProcesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
    const [summary, setSummary] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                ...(statusFilter ? { status: statusFilter } : {}),
                page,
                limit: pageSize,
                ...(search ? { q: search } : {}),
            };
            const [res, sum] = await Promise.all([
                apiClient.listCandidateProcesses(params),
                apiClient.getPipelineSummary().catch(() => null),
            ]);
            setProcesses(res.data || []);
            setPagination(res.pagination || { page, limit: pageSize, total: (res.data || []).length, totalPages: 1 });
            if (sum?.data) setSummary(sum.data);
        } catch (err) {
            toast.error(err.message || 'Failed to load candidate processes.');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, page, pageSize, search]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [statusFilter, pageSize, search]);

    // Search is server-side (`q` param) so pagination doesn't hide matches.
    const filtered = processes;

    const statusOptions = [
        { value: '',           label: 'All' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'passed',      label: 'Passed' },
        { value: 'rejected',    label: 'Rejected' },
        { value: 'on_hold',     label: 'On Hold' },
        { value: 'offer_made',  label: 'Offer Made' },
    ];

    // KPIs (prefer server summary so pagination doesn’t break totals)
    const total      = summary?.funnel?.total      ?? processes.length;
    const inProgress = summary?.funnel?.inProgress ?? processes.filter(p => p.status === 'in_progress').length;
    const hired      = summary?.funnel?.offerMade  ?? processes.filter(p => p.finalDecision === 'hire').length;
    const rejected   = summary?.funnel?.rejected   ?? processes.filter(p => p.finalDecision === 'reject').length;
    const onHold     = summary?.funnel?.onHold     ?? processes.filter(p => p.status === 'on_hold').length;

    if (loading) {
        return (
            <div className="space-y-6">
                <PageHeader
                    title="Candidate Pipeline"
                    description="Track all candidates across multi-round interview processes."
                />
                <PipelineListSkeleton />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Candidate Pipeline"
                description="Track all candidates across multi-round interview processes."
            />

            {/* KPI row */}
            <div className="grid gap-4 sm:grid-cols-5">
                {[
                    { label: 'Total',    value: total,      icon: Users,         color: 'text-blue-500' },
                    { label: 'Active',   value: inProgress, icon: Clock,         color: 'text-blue-500' },
                    { label: 'Hired',    value: hired,      icon: PartyPopper,   color: 'text-emerald-500' },
                    { label: 'Rejected', value: rejected,   icon: XCircle,       color: 'text-red-500' },
                    { label: 'On Hold',  value: onHold,     icon: Clock,         color: 'text-amber-500' },
                ].map(({ label, value, icon: Icon, color }) => (
                    <Card key={label}>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
                            <Icon className={cn('h-4 w-4', color)} />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{value}</div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search candidate or template…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <div className="flex gap-1 flex-wrap">
                        {statusOptions.map(opt => (
                            <Button
                                key={opt.value}
                                variant={statusFilter === opt.value ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setStatusFilter(opt.value)}
                            >
                                {opt.label}
                            </Button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {filtered.length === 0 ? (
                        <div className="py-16 text-center text-sm text-muted-foreground">
                            No candidate processes found.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Candidate</TableHead>
                                    <TableHead>Template</TableHead>
                                    <TableHead>Rounds</TableHead>
                                    <TableHead>Round</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Decision</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map(p => {
                                    const candidate = p.candidateId;
                                    const name = candidate
                                        ? `${candidate.firstName} ${candidate.lastName}`
                                        : 'Unknown';
                                    return (
                                        <TableRow
                                            key={p._id}
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => navigate(`/admin/candidate-process/${p._id}`)}
                                        >
                                            <TableCell>
                                                <div className="font-medium">{name}</div>
                                                <div className="text-xs text-muted-foreground">{candidate?.email}</div>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {p.templateId?.title || '—'}
                                            </TableCell>
                                            <TableCell>
                                                <RoundDots rounds={p.rounds} />
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {(() => {
                                                    // Use distinct roundNumbers attempted — never the raw
                                                    // currentRound field which can exceed plannedTotalRounds
                                                    // when old data had non-sequential master roundNumbers.
                                                    const uniqueRounds = new Set(
                                                        (p.rounds || []).map(r => r.roundNumber)
                                                    ).size;
                                                    const total = p.plannedTotalRounds || uniqueRounds;
                                                    return uniqueRounds
                                                        ? `${uniqueRounds} / ${total}`
                                                        : `0 / ${total}`;
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    {STATUS_ICON[p.status]}
                                                    <span className="text-sm capitalize">{p.status?.replace(/_/g, ' ')}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={cn('text-xs', DECISION_COLOR[p.finalDecision])}>
                                                    {p.finalDecision || 'pending'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                    Showing <span className="font-medium text-foreground">{processes.length}</span> of{' '}
                    <span className="font-medium text-foreground">{pagination.total}</span>
                    {' '}• Page <span className="font-medium text-foreground">{pagination.page}</span> of{' '}
                    <span className="font-medium text-foreground">{pagination.totalPages}</span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1">
                        {[10, 20, 50].map((n) => (
                            <Button
                                key={n}
                                variant={pageSize === n ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setPageSize(n)}
                            >
                                {n}/page
                            </Button>
                        ))}
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={pagination.page <= 1}
                        >
                            Prev
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.min(pagination.totalPages || p + 1, p + 1))}
                            disabled={pagination.page >= pagination.totalPages}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
